import { useState, useEffect } from "react";
import { api, apiError, downloadBlob } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, Clock, Images, Satellite, AlertTriangle, Gauge, Gem, Download } from "lucide-react";
import { cleanName } from "@/lib/format";

export const MAP_KEYS = [
  { key: "location", label: "Map 01 · Project Location" },
  { key: "spectral", label: "Map 02 · Remote Sensing" },
  { key: "target", label: "Map 03 · Priority Targets" },
  { key: "structural", label: "Map 04 · Structural / Alteration" },
  { key: "confidence", label: "Map 16 · Data Confidence" },
];

export function AreaMineralBlock({ project, area, commodities }) {
  const [sel, setSel] = useState(area.minerals || []);
  useEffect(() => { setSel(area.minerals || []); }, [area.minerals]);
  const save = async (next) => {
    setSel(next);
    try { await api.patch(`/projects/${project.id}/areas/${area.area_id}`, { minerals: next }); }
    catch (e) { toast.error(apiError(e)); }
  };
  const busy = area.minerals_status === "processing";
  const doneList = area.minerals_list || area.minerals || [];
  return (
    <div className="mt-3 pt-3 border-t border-border" data-testid={`area-mineral-block-${area.area_id}`}>
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold flex items-center gap-1"><Gem size={11} /> Target minerals</span>
        {!busy && (
          <Select value="" onValueChange={(m) => { if (m && !sel.includes(m)) save([...sel, m]); }}>
            <SelectTrigger className="h-7 w-44 text-xs" data-testid={`area-mineral-add-${area.area_id}`}><SelectValue placeholder="+ Add mineral" /></SelectTrigger>
            <SelectContent className="max-h-64">
              {commodities.filter((c) => !sel.includes(c.value)).map((c) => (
                <SelectItem key={c.value} value={c.value} className="text-xs">{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
      {sel.length ? (
        <div className="flex flex-wrap gap-1.5" data-testid={`area-mineral-chips-${area.area_id}`}>
          {sel.map((m) => (
            <span key={m} className="inline-flex items-center gap-1 text-[11px] font-semibold bg-[#C20511]/15 text-[#7a5b1e] border border-[#C20511]/40 rounded-full pl-2 pr-1 py-0.5">
              {m}{!busy && <button onClick={() => save(sel.filter((x) => x !== m))} data-testid={`area-mineral-rm-${area.area_id}-${m}`} className="hover:text-red-600"><XCircle size={12} /></button>}
            </span>
          ))}
        </div>
      ) : (
        <div className="text-[11px] text-muted-foreground">No minerals selected - pick minerals and they&apos;ll be analysed automatically when you Generate.</div>
      )}

      {busy && (
        <div className="mt-2 text-[11px] text-[#7a5b1e] flex items-center gap-1.5" data-testid={`area-minerals-progress-${area.area_id}`}>
          <Loader2 size={12} className="animate-spin" /> Minerals: {area.minerals_done || 0}/{area.minerals_total || sel.length} · analysing {area.minerals_current || "…"}
        </div>
      )}

      {area.minerals_status === "complete" && doneList.length > 0 && (
        <div className="mt-2.5" data-testid={`area-minerals-done-${area.area_id}`}>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">Mineral reports (PDF)</div>
          <div className="flex flex-wrap gap-1.5">
            {doneList.map((m) => (
              <button key={m} data-testid={`area-mineral-report-${area.area_id}-${m}`}
                onClick={() => downloadBlob(`/projects/${project.id}/areas/${area.area_id}/minerals/${encodeURIComponent(m)}/report.pdf`, `${area.area_id}_${m}_report.pdf`).catch((e) => toast.error(apiError(e)))}
                className="inline-flex items-center gap-1 text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5 hover:bg-emerald-100">
                <Download size={11} /> {m}
              </button>
            ))}
          </div>
        </div>
      )}
      {area.minerals_status === "error" && (
        <div className="mt-2 text-[11px] text-red-600" data-testid={`area-minerals-error-${area.area_id}`}>Mineral analysis failed - re-run from the Minerals dialog.</div>
      )}
    </div>
  );
}

export function ImageryDialog({ project, area, onClose, reload }) {
  const [provider, setProvider] = useState("sentinel-2");
  const [providers, setProviders] = useState([]);
  const [from, setFrom] = useState("2024-01-01");
  const [to, setTo] = useState("2024-12-31");
  const [cloud, setCloud] = useState(60);
  const [loading, setLoading] = useState(false);
  const [scenes, setScenes] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    if (!area) { setScenes([]); setSelectedId(null); return; }
    setSelectedId(area.selected_scene?.scene_id || area.selected_scene?.id || null);
    setPreview(null);
    api.get("/catalog").then(({ data }) => setProviders((data.satellite_providers || []).filter((p) => !/landsat/i.test((p.key || "") + (p.name || ""))))).catch(() => {});
  }, [area]);

  if (!area) return null;

  const search = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/projects/${project.id}/areas/${area.area_id}/satellite/search`, {
        provider, date_from: from, date_to: to, max_cloud: Number(cloud),
      });
      setScenes(data.scenes || []);
      toast.success(`${data.count} scene(s) found for ${area.area_id}`);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };

  const select = async (scene) => {
    try {
      await api.post(`/projects/${project.id}/areas/${area.area_id}/satellite/select`, { scene });
      setSelectedId(scene.scene_id || scene.id);
      toast.success("Imagery selected - it will be used when you generate this area");
      reload && reload();
    } catch (e) { toast.error(apiError(e)); }
  };

  const loadPreview = async () => {
    setPreviewLoading(true);
    try {
      const { data } = await api.get(`/projects/${project.id}/areas/${area.area_id}/imagery-preview`);
      setPreview(data);
    } catch (e) { toast.error(apiError(e)); } finally { setPreviewLoading(false); }
  };

  return (
    <Dialog open={!!area} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl w-[92vw] max-h-[90vh] p-0 overflow-hidden flex flex-col" data-testid="area-imagery-dialog">
        <DialogTitle className="px-6 py-4 border-b border-border dark:border-slate-700 font-bold shrink-0">
          {area.area_id} · Select Satellite Imagery
        </DialogTitle>
        <DialogDescription className="sr-only">Search the satellite archive and pick the best-covering, low-cloud scene for this licence area.</DialogDescription>
        <div className="p-5 overflow-auto space-y-4">
          <p className="text-xs text-muted-foreground">Search the free satellite archive for this licence and pick the best-covering, low-cloud scene. If you don&apos;t select one, the app auto-picks the best available scene.</p>
          {area.selected_scene && (
            <div className="flex items-center gap-2 text-xs bg-primary/10 border border-primary/30 rounded-lg px-3 py-2" data-testid="area-imagery-current">
              <CheckCircle2 size={15} className="text-primary shrink-0" />
              <span className="text-foreground truncate">Currently selected: <span className="font-mono">{area.selected_scene.scene_id || area.selected_scene.id}</span> · {(area.selected_scene.datetime || "").slice(0, 10)} · ☁ {area.selected_scene.cloud_cover ?? "-"}% · cov {area.selected_scene.coverage_pct ?? "-"}%</span>
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Satellite / Sensor</Label>
              <Select value={provider} onValueChange={setProvider}>
                <SelectTrigger className="h-9 mt-1" data-testid="area-sat-provider"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(providers.length ? providers : [{ key: "sentinel-2", name: "Sentinel-2" }]).map((p) => (
                    <SelectItem key={p.key} value={p.key}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Max Cloud Cover: {cloud}%</Label>
              <Input type="range" min="0" max="100" value={cloud} onChange={(e) => setCloud(e.target.value)} className="mt-2.5" data-testid="area-sat-cloud" />
            </div>
            <div><Label className="text-xs">Date From</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 mt-1" data-testid="area-sat-from" /></div>
            <div><Label className="text-xs">Date To</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 mt-1" data-testid="area-sat-to" /></div>
          </div>
          <Button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground" onClick={search} disabled={loading} data-testid="area-sat-search">
            {loading ? <><Loader2 size={16} className="mr-2 animate-spin" />Searching real archives…</> : <><Satellite size={16} className="mr-2" />Search Available Imagery</>}
          </Button>

          <div className="border border-border rounded-lg p-3 bg-muted/50" data-testid="imagery-preview-block">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-xs font-semibold text-foreground flex items-center gap-1.5"><Images size={14} /> Cloud-Removal Preview</div>
              <Button size="sm" variant="outline" className="h-8 text-xs border-border" onClick={loadPreview} disabled={previewLoading} data-testid="imagery-preview-btn">
                {previewLoading ? <><Loader2 size={14} className="mr-1.5 animate-spin" />Rendering…</> : "Generate Before / After"}
              </Button>
            </div>
            {preview && (
              <div className="grid grid-cols-2 gap-3 mt-3" data-testid="imagery-preview">
                <figure><img src={preview.before} alt="before cloud removal" className="w-full rounded border border-border" /><figcaption className="text-[10px] text-muted-foreground mt-1 text-center">Before · single scene (clouds visible)</figcaption></figure>
                <figure><img src={preview.after} alt="after cloud removal" className="w-full rounded border border-primary/40" /><figcaption className="text-[10px] text-primary font-medium mt-1 text-center">After · {preview.scenes}-scene median composite (cloud-free)</figcaption></figure>
              </div>
            )}
          </div>

          <div className="space-y-2">
            {scenes.length > 0 && (
              <div className="flex items-center justify-between flex-wrap gap-1">
                <p className="text-[11px] font-semibold tracking-wide text-muted-foreground uppercase">{scenes.length} scenes · best coverage first</p>
                <p className="text-[10px] text-muted-foreground flex items-center gap-2" data-testid="footprint-legend">
                  <span className="inline-block w-3 h-3 rounded-sm" style={{ background: "rgba(201,154,60,0.35)", border: "1.5px solid #C20511" }} /> licence
                  <span className="inline-block w-3 h-3 rounded-sm" style={{ background: "rgba(148,163,184,0.45)", border: "1px solid #94a3b8" }} /> scene extent
                </p>
              </div>
            )}
            {scenes.map((s) => {
              const sid = s.scene_id || s.id;
              const sel = sid === selectedId;
              return (
                <div key={sid} data-testid={`area-scene-${sid}`}
                  className={`flex gap-3 p-2.5 rounded-lg border transition-colors ${sel ? "border-primary bg-primary/10" : "border-border hover:border-primary/60"}`}>
                  <div className="w-16 h-16 rounded bg-muted overflow-hidden shrink-0">
                    {s.thumbnail ? <img src={s.thumbnail} alt="preview" className="w-full h-full object-cover" /> : null}
                  </div>
                  <Footprint area={area.boundary_metrics?.bbox} scene={s.bbox} />
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-[11px] truncate text-foreground">{sid}</div>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <Badge variant="secondary" className="text-[10px]">{(s.datetime || "").slice(0, 10)}</Badge>
                      <Badge variant="outline" className="text-[10px]">☁ {s.cloud_cover ?? "-"}%</Badge>
                      <Badge variant="outline" className="text-[10px]">cov {s.coverage_pct ?? "-"}%</Badge>
                      {s.quality_score != null && <Badge variant="outline" className="text-[10px]">Q {s.quality_score}</Badge>}
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-1">{s.platform} · {Object.keys(s.assets || {}).length} bands</div>
                  </div>
                  <Button size="sm" variant={sel ? "secondary" : "outline"} className="self-center h-8 text-xs" onClick={() => select(s)} data-testid={`area-select-scene-${sid}`}>
                    {sel ? <><CheckCircle2 size={15} className="mr-1 text-primary" />Selected</> : "Select"}
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function Footprint({ area, scene }) {
  if (!area || !scene || area.length < 4 || scene.length < 4) return null;
  const minx = Math.min(area[0], scene[0]), miny = Math.min(area[1], scene[1]);
  const maxx = Math.max(area[2], scene[2]), maxy = Math.max(area[3], scene[3]);
  const W = (maxx - minx) || 1, H = (maxy - miny) || 1;
  const S = 60, pad = 4, span = S - pad * 2;
  const nx = (x) => pad + ((x - minx) / W) * span;
  const ny = (y) => pad + (1 - (y - miny) / H) * span;
  const rectFor = (b, fill, stroke, sw) => (
    <rect x={nx(b[0])} y={ny(b[3])} width={Math.max(2, nx(b[2]) - nx(b[0]))} height={Math.max(2, ny(b[1]) - ny(b[3]))}
      fill={fill} stroke={stroke} strokeWidth={sw} rx="1.5" />
  );
  return (
    <svg width={S} height={S} viewBox={`0 0 ${S} ${S}`} className="rounded bg-muted border border-border shrink-0" data-testid="scene-footprint" title="Scene footprint over licence">
      {rectFor(scene, "rgba(148,163,184,0.45)", "#94a3b8", 1)}
      {rectFor(area, "rgba(201,154,60,0.35)", "#C20511", 1.6)}
    </svg>
  );
}

export function CoverageBadge({ a, probing }) {
  if (["queued", "processing"].includes(a.status)) return null;
  const c = a.imagery_coverage_pct;
  if (c == null) {
    return probing ? (
      <div className="flex items-center gap-1.5 text-[11px] font-semibold text-muted-foreground shrink-0" data-testid={`area-coverage-checking-${a.area_id}`}>
        <Loader2 size={13} className="animate-spin" /> Checking…
      </div>
    ) : null;
  }
  const tone = c >= 95
    ? ["text-emerald-700", "bg-emerald-50", "border-emerald-200", CheckCircle2]
    : c >= 80
      ? ["text-red-700", "bg-red-50", "border-red-200", AlertTriangle]
      : ["text-red-700", "bg-red-50", "border-red-200", AlertTriangle];
  const [tc, bg, bd, Icon] = tone;
  const title = c >= 95
    ? "Imagery covers effectively the whole licence - no grey gaps."
    : `Best available imagery covers ${c}% of the licence. The rest has no cloud-free satellite data or is water - it will appear as a masked 'no-data / water' area on the maps, not as an error.`;
  return (
    <div className={`inline-flex items-center gap-1 text-[11px] font-bold rounded-full px-2.5 py-1 border shrink-0 ${tc} ${bg} ${bd}`}
      data-testid={`area-coverage-badge-${a.area_id}`} title={title}>
      <Icon size={12} /> {c}%{a.imagery_widened ? <span className="font-medium opacity-70" title="Date range was auto-widened to fill gaps"> ·↗</span> : null}
    </div>
  );
}

export function StatusChip({ a }) {
  const map = {
    complete: ["text-emerald-600", CheckCircle2, "Complete"],
    processing: ["text-primary", Loader2, "Processing"],
    queued: ["text-muted-foreground", Clock, "Waiting"],
    failed: ["text-red-600", XCircle, "Failed"],
    error: ["text-red-600", XCircle, "Invalid"],
    validated: ["text-muted-foreground", Clock, "Ready"],
  };
  const [cls, Icon, label] = map[a.status] || map.validated;
  return (
    <div className={`flex items-center gap-1.5 text-xs font-semibold ${cls}`} data-testid={`area-chip-${a.area_id}`}>
      <Icon size={15} className={a.status === "processing" ? "animate-spin" : ""} /> {label}
    </div>
  );
}

let _planetStatusPromise = null;
export function PlanetPreview({ a }) {
  const [on, setOn] = useState(false);
  const [open, setOpen] = useState(false);
  const [scenes, setScenes] = useState(null);
  const [quota, setQuota] = useState(null);
  useEffect(() => {
    if (!_planetStatusPromise) _planetStatusPromise = api.get("/planet/status").then((r) => !!r.data?.configured).catch(() => false);
    _planetStatusPromise.then(setOn);
  }, []);
  const openDlg = async () => {
    setOpen(true); setScenes(null);
    api.get("/planet/quota").then((r) => setQuota(r.data)).catch(() => setQuota(null));
    try {
      const bbox = (a.boundary_metrics || {}).bbox;
      const { data } = await api.post(`/planet/search`, { bbox, days: 365, cloud_cover: 0.3 });
      setScenes(data.scenes || []);
    } catch (e) { toast.error(apiError(e)); setScenes([]); }
  };
  if (!on) return null;
  return (<>
    <Button size="sm" variant="outline" className="border-violet-300 text-violet-700 hover:bg-violet-50 h-7 text-xs shrink-0" onClick={openDlg} data-testid={`area-planet-${a.area_id}`}>
      <Gauge size={13} className="mr-1.5" /> 3 m Planet
    </Button>
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl" data-testid="planet-dialog">
        <DialogTitle className="font-display">PlanetScope 3 m - {cleanName(a.name)}</DialogTitle>
        <DialogDescription className="text-xs">
          Latest high-resolution PlanetScope passes over this licence (premium sensor - downloads use your Planet quota).
        </DialogDescription>
        {quota?.available && (
          <div className="flex items-center gap-2 text-[11px] rounded-md border border-violet-200 bg-violet-50 text-violet-800 px-2.5 py-1.5" data-testid="planet-quota">
            <span className="font-bold">Quota:</span>
            <span>{quota.remaining_sqkm} km² remaining of {quota.quota_sqkm} km²{quota.pct_used != null ? ` (${quota.pct_used}% used)` : ""}</span>
            {quota.pct_used != null && quota.pct_used >= 85 && <span className="font-bold text-red-600">· Low quota!</span>}
          </div>
        )}
        {scenes === null ? (
          <div className="text-sm text-muted-foreground py-8 text-center" data-testid="planet-searching">Searching Planet catalogue…</div>
        ) : scenes.length === 0 ? (
          <div className="text-sm text-muted-foreground py-8 text-center" data-testid="planet-empty">No PlanetScope scenes found for this area / filter.</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[420px] overflow-y-auto pr-1">
            {scenes.map((s) => (
              <div key={s.id} className="border border-border rounded-lg overflow-hidden" data-testid={`planet-scene-${s.id}`}>
                <img src={`${process.env.REACT_APP_BACKEND_URL}/api/planet/preview/${s.id}`} alt={s.id}
                  className="h-28 w-full object-cover bg-muted" loading="lazy" />
                <div className="p-2 text-[11px]">
                  <div className="font-mono truncate">{(s.acquired || "").slice(0, 10)} · ☁ {Math.round((s.cloud_cover || 0) * 100)}%</div>
                  <div className="text-muted-foreground">{s.pixel_res_m || 3} m/px native</div>
                  <button className="mt-1 text-violet-700 font-semibold hover:underline" data-testid={`planet-dl-${s.id}`}
                    onClick={async () => {
                      if (quota?.available && !window.confirm(
                        `Downloading this scene spends your Planet quota (${quota.remaining_sqkm} km\u00b2 remaining of ${quota.quota_sqkm} km\u00b2). Continue?`)) return;
                      try {
                        const { data } = await api.post(`/planet/scenes/${s.id}/activate`);
                        if (data.status === "active") {
                          toast.success("Asset active - downloading GeoTIFF");
                          window.open(`${process.env.REACT_APP_BACKEND_URL}/api/planet/scenes/${s.id}/download`, "_blank");
                        } else toast.success("Activating on Planet - retry the download in a few minutes");
                      } catch (e) { toast.error(apiError(e)); }
                    }}>Activate &amp; download GeoTIFF</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  </>);
}
